/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      helper — `(ec-language-name lang)`: the name of an instructor
 *              language in the shop's current locale, for the `alt` of its
 *              flag (impeccable audit 2026-10-19: the flags had alt='')
 *   extends:   Helper (@ember/component/helper)
 *   template:  none
 *   uses:      service:intl (primaryLocale: tracked, recomputes on a language
 *              switch), Intl.DisplayNames
 *   siblings:  helpers/format-date.js (same primaryLocale tracking idiom)
 *   tests:     tests/acceptance/ui-nuova-ec-product-test.js
 *   gotchas:   the API stores a language as the Italian name of its flag file
 *              ('italiano', 'inglese' … = flag/<lang>.png) or as an ISO code
 *              ('it'): both resolve to the ISO code; an unknown value is shown
 *              as it is (capitalised), never an empty alt.
 */
import Helper from '@ember/component/helper';
import { service } from '@ember/service';

const ITALIAN_NAME_TO_ISO = {
  italiano: 'it',
  inglese: 'en',
  francese: 'fr',
  tedesco: 'de',
  spagnolo: 'es',
  portoghese: 'pt',
  olandese: 'nl',
  russo: 'ru',
  polacco: 'pl',
  ceco: 'cs',
  slovacco: 'sk',
  sloveno: 'sl',
  croato: 'hr',
  serbo: 'sr',
  ungherese: 'hu',
  rumeno: 'ro',
  bulgaro: 'bg',
  greco: 'el',
  turco: 'tr',
  svedese: 'sv',
  norvegese: 'no',
  danese: 'da',
  finlandese: 'fi',
  ucraino: 'uk',
  cinese: 'zh',
  giapponese: 'ja',
  coreano: 'ko',
  arabo: 'ar',
  ebraico: 'he',
  hindi: 'hi',
  catalano: 'ca',
};

export default class EcLanguageNameHelper extends Helper {
  @service intl;

  compute([lang]) {
    const locale = this.intl.primaryLocale || 'it-IT'; // tracked: recompute on a language switch
    const raw = String(lang ?? '').trim();

    if (!raw) {
      return '';
    }

    const key = raw.toLowerCase();
    const iso = ITALIAN_NAME_TO_ISO[key] || (/^[a-z]{2,3}$/.test(key) ? key : null);

    if (iso && typeof Intl?.DisplayNames === 'function') {
      try {
        const name = new Intl.DisplayNames([locale], { type: 'language' }).of(iso);

        if (name && name.toLowerCase() !== iso) {
          return name.charAt(0).toUpperCase() + name.slice(1);
        }
      } catch {
        // an invalid locale / code falls through to the raw value
      }
    }

    return raw.charAt(0).toUpperCase() + raw.slice(1);
  }
}
